/**
 * MealPlannerPage Component
 * Main meal planning interface with tabs for different workflow stages
 */

import React, { useState, useEffect } from "react";
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import { GenerateMealPlan } from "./GenerateMealPlan";
import { MealPlanEditor } from "./MealPlanEditor";
import { MealPlanHistory } from "./MealPlanHistory";
import { OtherTools } from "./OtherTools";
import { MealPlan, mealPlanApi } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useUnsavedChanges } from "../contexts/UnsavedChangesContext";
import { useSubscription } from "../contexts/SubscriptionContext";
import { hasServiceAccess, getUserAccessStatus } from "../utils";
import { APP_CONFIG } from "../config/app.config";

type TabType = "generate" | "editor" | "history" | "preview" | "ingredients" | "tools";

export const MealPlannerPage: React.FC = () => {
    const navigate = useNavigate();
    const { tab } = useParams<{ tab?: string }>();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { dbUser } = useAuth();
    const { checkAndNavigate } = useUnsavedChanges();
    const { openSubscriptionModal } = useSubscription();
    const [selectedMealPlan, setSelectedMealPlan] = useState<MealPlan | null>(
        null
    );
    const [loadingMealPlan, setLoadingMealPlan] = useState(false);
    const [mealPlanError, setMealPlanError] = useState<string | null>(null);

    // Check if user has access to the service
    const hasAccess = hasServiceAccess(dbUser);
    const accessStatus = getUserAccessStatus(dbUser);
    
    // Determine which tabs should be accessible
    // History and Editor should be accessible to users who have used free trial (has credit card)
    const canAccessHistoryEditor = hasAccess || (accessStatus.hasUsedFreeTrial && accessStatus.hasValidPaymentMethod);
    const canAccessGenerate = hasAccess;

    // Determine active tab from URL
    const validTabs = [
        "generate",
        "editor",
        "history",
        "preview",
        "ingredients",
        "tools",
    ];
    const activeTab: TabType = (
        validTabs.includes(tab || "") ? tab : "generate"
    ) as TabType;

    // Handle URL redirects and meal plan loading
    useEffect(() => {
        const mealPlanId = searchParams.get("id");

        // Redirect bare /meal-planner to appropriate tab
        if (location.pathname === "/meal-planner") {
            // Redirect to history if no generate access, otherwise to generate
            const defaultTab = canAccessGenerate ? "generate" : "history";
            navigate(`/meal-planner/${defaultTab}`, { replace: true });
            return;
        }

        // Don't redirect from generate tab - let it show with overlay
        // Only redirect if trying to access history/editor without permission
        
        // Users who have used free trial can access history and editor
        if ((activeTab === "history" || activeTab === "editor") && !canAccessHistoryEditor) {
            navigate("/meal-planner/generate", { replace: true });
            return;
        }

        // Load meal plan from query parameter if on editor tab
        const loadMealPlanFromUrl = async () => {
            if (mealPlanId && dbUser?.id && activeTab === "editor") {
                setLoadingMealPlan(true);
                setMealPlanError(null);
                try {
                    const mealPlan = await mealPlanApi.getMealPlan(
                        dbUser.id,
                        parseInt(mealPlanId)
                    );
                    setSelectedMealPlan(mealPlan);
                    setMealPlanError(null); // Clear any previous errors
                } catch (error) {
                    console.error("Failed to load meal plan:", error);
                    setMealPlanError("Meal plan not found");
                    // Show error briefly before redirecting
                    setTimeout(() => {
                        navigate("/meal-planner/history", { replace: true });
                    }, 2000);
                }
                setLoadingMealPlan(false);
            } else if (!mealPlanId && activeTab === "editor") {
                // Clear meal plan when on editor without ID
                setSelectedMealPlan(null);
                setMealPlanError(null);
            }
        };

        loadMealPlanFromUrl();
    }, [searchParams, dbUser?.id, activeTab, navigate, location.pathname, canAccessGenerate, canAccessHistoryEditor]);

    // Handle navigation state (for toast/task notifications)
    useEffect(() => {
        if (location.state) {
            const {
                activeTab: navTab,
                mealPlan,
            } = location.state as any;

            if (mealPlan) {
                setSelectedMealPlan(mealPlan);
                // Navigate to editor with meal plan ID
                navigate(`/meal-planner/editor?id=${mealPlan.id}`, {
                    replace: true,
                });
            } else if (navTab && navTab !== activeTab) {
                // Navigate to the specified tab
                navigate(`/meal-planner/${navTab}`, { replace: true });
            }

            // Clear navigation state
            window.history.replaceState({}, document.title);
        }
    }, [location.state, navigate, activeTab]);

    const handleMealPlanSelect = (mealPlan: MealPlan) => {
        setSelectedMealPlan(mealPlan);
        // Navigate to editor with meal plan ID
        navigate(`/meal-planner/editor?id=${mealPlan.id}`, { replace: true });
    };

    const handleSaveMealPlan = () => {
        // Save logic is now handled internally by MealPlanEditor
        // This callback handles post-save actions
        // After saving, redirect to history to see updated list
        setSelectedMealPlan(null); // Clear selection
        navigate("/meal-planner/history", { replace: true });
    };

    const handleCancelEdit = () => {
        setSelectedMealPlan(null);
        // Navigate back to history
        navigate("/meal-planner/history", { replace: true });
    };

    const handleTabClick = (tabId: TabType) => {
        // Check access based on tab type
        // Allow clicking Generate tab even without access (will show overlay)
        // Tools tab should be accessible to users who have used free trial (has credit card)
        if ((tabId === "history" || tabId === "editor") && !canAccessHistoryEditor) {
            return;
        }
        if (tabId === "tools" && !canAccessHistoryEditor) {
            return;
        }
        checkAndNavigate(`/meal-planner/${tabId}`);
    };

    const tabs = [
        {
            id: "generate" as TabType,
            label: "⚡ Generate",
            component: <GenerateMealPlan />,
        },
        {
            id: "editor" as TabType,
            label: "✏ Editor",
            component: (
                <MealPlanEditor
                    key={selectedMealPlan?.id || 'new'}
                    mealPlan={selectedMealPlan}
                    onSave={handleSaveMealPlan}
                    onCancel={handleCancelEdit}
                />
            ),
        },
        {
            id: "history" as TabType,
            label: "📋 History",
            component: (
                <MealPlanHistory onMealPlanSelect={handleMealPlanSelect} />
            ),
        },
        {
            id: "tools" as TabType,
            label: "🛠️ Other Tools",
            component: <OtherTools />,
        },
        // Hidden tabs (keep components but don't show in navigation)
        // {
        //     id: "preview" as TabType,
        //     label: "👁️ Preview Mode",
        //     component: <MealPlanPreview />,
        // },
        // {
        //     id: "ingredients" as TabType,
        //     label: "🔍 Ingredient Search",
        //     component: <IngredientSearchTest />,
        // },
    ];

    const containerStyle: React.CSSProperties = {
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        padding: "24px",
        minHeight: "600px",
    };

    const headerStyle: React.CSSProperties = {
        marginBottom: "32px",
        paddingBottom: "20px",
        borderBottom: "1px solid #e5e7eb",
    };

    const titleStyle: React.CSSProperties = {
        fontSize: "32px",
        fontWeight: "600",
        color: "#1a202c",
        marginBottom: "8px",
    };

    const descriptionStyle: React.CSSProperties = {
        color: "#6b7280",
        fontSize: "16px",
        marginBottom: "24px",
    };

    const tabsContainerStyle: React.CSSProperties = {
        display: "flex",
        borderBottom: "1px solid #e5e7eb",
        marginBottom: "32px",
    };

    const tabButtonStyle: React.CSSProperties = {
        padding: "16px 24px",
        border: "none",
        backgroundColor: "transparent",
        color: "#6b7280",
        cursor: "pointer",
        fontSize: "16px",
        fontWeight: "500",
        borderBottomWidth: "3px",
        borderBottomStyle: "solid",
        borderBottomColor: "transparent",
        transition: "all 0.2s",
        flex: 1,
        textAlign: "center",
    };

    const activeTabStyle: React.CSSProperties = {
        ...tabButtonStyle,
        color: "#3b82f6",
        borderBottomColor: "#3b82f6",
        backgroundColor: "#eff6ff",
    };

    const contentStyle: React.CSSProperties = {
        minHeight: "500px",
    };

    const renderContent = () => {
        // For generate tab, show upgrade prompt if no access
        if (activeTab === "generate" && !canAccessGenerate) {
            return <UpgradePrompt />;
        }
        
        // For other tabs, show the component if user has access
        const currentTab = tabs.find((tab) => tab.id === activeTab);
        return currentTab?.component || <div>Tab not found</div>;
    };

    const UpgradePrompt = () => {
        // Different messaging based on user's status
        const showFreeTrialCTA = accessStatus.canGetFreeTrial;
        const hasUsedFreeTrial = accessStatus.hasUsedFreeTrial;
        
        return (
            <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px 20px",
                textAlign: "center",
                maxWidth: "800px",
                margin: "0 auto",
            }}>
                {showFreeTrialCTA ? (
                    // Free Trial Design
                    <>
                        {/* Eye-catching header with badge */}
                        <div style={{
                            position: "relative",
                            marginBottom: "24px",
                        }}>
                            <div style={{
                                position: "absolute",
                                top: "-12px",
                                right: "-40px",
                                backgroundColor: "#fbbf24",
                                color: "#78350f",
                                padding: "4px 16px",
                                borderRadius: "20px",
                                fontSize: "14px",
                                fontWeight: "700",
                                transform: "rotate(12deg)",
                                boxShadow: "0 2px 8px rgba(251, 191, 36, 0.3)",
                            }}>
                                LIMITED TIME
                            </div>
                            <h2 style={{
                                fontSize: "40px",
                                fontWeight: "800",
                                color: "#059669",
                                marginBottom: "8px",
                                lineHeight: "1.1",
                            }}>
                                🎉 Get Your First 3 Meal Plans
                                <span style={{
                                    display: "block",
                                    fontSize: "48px",
                                    color: "#10b981",
                                    textTransform: "uppercase",
                                    letterSpacing: "2px",
                                    marginTop: "8px",
                                }}>
                                    100% FREE
                                </span>
                            </h2>
                        </div>

                        {/* Value proposition */}
                        <p style={{
                            fontSize: "20px",
                            color: "#374151",
                            marginBottom: "32px",
                            lineHeight: "1.5",
                            fontWeight: "500",
                        }}>
                            No credit card charges • No hidden fees • Cancel anytime
                        </p>

                        {/* Main CTA Box */}
                        <div style={{
                            background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                            borderRadius: "20px",
                            padding: "32px",
                            marginBottom: "32px",
                            border: "3px solid #10b981",
                            width: "100%",
                            boxShadow: "0 10px 30px rgba(16, 185, 129, 0.15)",
                        }}>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: "24px",
                            }}>
                                {/* Left side - Benefits */}
                                <div style={{
                                    flex: "1 1 300px",
                                    textAlign: "left",
                                }}>
                                    <div style={{
                                        fontSize: "24px",
                                        fontWeight: "700",
                                        color: "#047857",
                                        marginBottom: "16px",
                                    }}>
                                        What You Get:
                                    </div>
                                    <div>
                                        {[
                                            "3 AI-generated personalized meal plans",
                                            "Complete nutrition tracking & macros",
                                            "Smart ingredient substitutions",
                                            "Save & edit your meal plans",
                                        ].map((benefit, idx) => (
                                            <div key={idx} style={{
                                                display: "flex",
                                                alignItems: "center",
                                                marginBottom: "12px",
                                                fontSize: "16px",
                                                color: "#065f46",
                                            }}>
                                                <div style={{
                                                    width: "24px",
                                                    height: "24px",
                                                    borderRadius: "50%",
                                                    backgroundColor: "#10b981",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    marginRight: "12px",
                                                    flexShrink: 0,
                                                }}>
                                                    <svg width="14" height="14" viewBox="0 0 20 20" fill="white">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                                {benefit}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Right side - CTA */}
                                <div style={{
                                    flex: "0 0 auto",
                                    textAlign: "center",
                                }}>
                                    <div style={{
                                        fontSize: "18px",
                                        color: "#059669",
                                        marginBottom: "8px",
                                        fontWeight: "600",
                                    }}>
                                        Start Now
                                    </div>
                                    <div style={{
                                        fontSize: "72px",
                                        fontWeight: "900",
                                        color: "#10b981",
                                        lineHeight: "1",
                                        marginBottom: "8px",
                                    }}>
                                        $0
                                    </div>
                                    <div style={{
                                        fontSize: "16px",
                                        color: "#047857",
                                        marginBottom: "16px",
                                    }}>
                                        today
                                    </div>
                                    <button
                                        onClick={openSubscriptionModal}
                                        style={{
                                            backgroundColor: "#10b981",
                                            color: "white",
                                            padding: "16px 32px",
                                            borderRadius: "12px",
                                            fontSize: "18px",
                                            fontWeight: "700",
                                            border: "none",
                                            cursor: "pointer",
                                            transition: "all 0.2s",
                                            boxShadow: "0 4px 14px rgba(16, 185, 129, 0.4)",
                                            textTransform: "uppercase",
                                            letterSpacing: "1px",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = "#059669";
                                            e.currentTarget.style.transform = "translateY(-2px)";
                                            e.currentTarget.style.boxShadow = "0 6px 20px rgba(16, 185, 129, 0.5)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = "#10b981";
                                            e.currentTarget.style.transform = "translateY(0)";
                                            e.currentTarget.style.boxShadow = "0 4px 14px rgba(16, 185, 129, 0.4)";
                                        }}
                                    >
                                        Claim Free Trial
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Trust signals */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "32px",
                            marginBottom: "32px",
                            flexWrap: "wrap",
                        }}>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                color: "#6b7280",
                                fontSize: "14px",
                            }}>
                                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                </svg>
                                Secure payment
                            </div>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                color: "#6b7280",
                                fontSize: "14px",
                            }}>
                                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                No auto-charges
                            </div>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                color: "#6b7280",
                                fontSize: "14px",
                            }}>
                                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                                </svg>
                                Multiple Profiles
                            </div>
                        </div>

                        {/* After free trial info */}
                        <div style={{
                            backgroundColor: "#f3f4f6",
                            borderRadius: "12px",
                            padding: "20px",
                            width: "100%",
                            marginBottom: "24px",
                        }}>
                            <p style={{
                                fontSize: "14px",
                                color: "#4b5563",
                                margin: 0,
                            }}>
                                <strong>After your 3 free meal plans:</strong> Subscribe for just ${APP_CONFIG.SUBSCRIPTION_PRICE}/month ({APP_CONFIG.SUBSCRIPTION_DISCOUNT_PERCENT}% off regular price) or simply stop using the service. No surprises, no commitments.
                            </p>
                        </div>

                        {/* Features showcase */}
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                            gap: "20px",
                            width: "100%",
                        }}>
                            <FeatureCard 
                                icon="⚡"
                                title="AI-Powered"
                                description="Personalized plans in seconds"
                            />
                            <FeatureCard 
                                icon="📊"
                                title="Full Nutrition"
                                description="Track macros & calories"
                            />
                            <FeatureCard 
                                icon="🔄"
                                title="Flexible"
                                description="Edit & regenerate anytime"
                            />
                        </div>
                    </>
                ) : (
                    // Regular subscription design for non-free-trial users
                    <>
                        <h2 style={{
                            fontSize: "32px",
                            fontWeight: "700",
                            color: "#1a202c",
                            marginBottom: "16px",
                            lineHeight: "1.2",
                        }}>
                            Unlock AI-Powered Meal Planning
                        </h2>

                        <p style={{
                            fontSize: "18px",
                            color: "#4a5568",
                            marginBottom: "32px",
                            lineHeight: "1.6",
                        }}>
                            {hasUsedFreeTrial
                            ? "You've used your free trial. Subscribe to continue creating personalized meal plans and tracking nutrition."
                            : "Subscribe to access personalized meal plans, smart nutrition tracking, and AI-generated recipes tailored to your dietary preferences."}
                        </p>

                        <div style={{
                            backgroundColor: "#f7fafc",
                            borderRadius: "16px",
                            padding: "32px",
                            marginBottom: "32px",
                            border: "2px solid #e2e8f0",
                            width: "100%",
                        }}>
                            <div style={{
                                fontSize: "14px",
                                color: "#718096",
                                marginBottom: "8px",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                            }}>
                                Monthly Subscription
                            </div>
                            <div style={{
                                fontSize: "48px",
                                fontWeight: "700",
                                color: "#3b82f6",
                                marginBottom: "8px",
                            }}>
                                ${APP_CONFIG.SUBSCRIPTION_PRICE}
                            </div>
                            <div style={{
                                display: "inline-block",
                                backgroundColor: "#10b981",
                                color: "white",
                                padding: "4px 12px",
                                borderRadius: "20px",
                                fontSize: "14px",
                                fontWeight: "600",
                                marginBottom: "16px",
                            }}>
                                {APP_CONFIG.SUBSCRIPTION_DISCOUNT_PERCENT}% OFF regular pricing
                            </div>
                            <div style={{
                                fontSize: "16px",
                                color: "#4a5568",
                            }}>
                                4 million tokens per month
                            </div>
                        </div>

                        <button
                            onClick={openSubscriptionModal}
                            style={{
                                backgroundColor: "#3b82f6",
                                color: "white",
                                padding: "16px 48px",
                                borderRadius: "8px",
                                fontSize: "18px",
                                fontWeight: "600",
                                border: "none",
                                cursor: "pointer",
                                transition: "all 0.2s",
                                boxShadow: "0 4px 14px rgba(59, 130, 246, 0.3)",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#2563eb";
                                e.currentTarget.style.transform = "translateY(-2px)";
                                e.currentTarget.style.boxShadow = "0 6px 20px rgba(59, 130, 246, 0.4)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "#3b82f6";
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "0 4px 14px rgba(59, 130, 246, 0.3)";
                            }}
                        >
                            Subscribe Now
                        </button>

                        <p style={{
                            fontSize: "14px",
                            color: "#718096",
                            marginTop: "16px",
                        }}>
                            Cancel anytime • Instant access
                        </p>

                        <div style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "20px",
                            width: "100%",
                            marginTop: "48px",
                            justifyContent: "center",
                        }}>
                            <FeatureCard 
                                icon="⚡"
                                title="AI Meal Generation"
                                description="Create personalized meal plans in seconds"
                            />
                            <FeatureCard 
                                icon="📊"
                                title="Nutrition Tracking"
                                description="Monitor macros and calories automatically"
                            />
                            <FeatureCard 
                                icon="🔄"
                                title="Smart Regeneration"
                                description="Refine meals with one click"
                            />
                        </div>
                    </>
                )}
            </div>
        );
    };

    const FeatureCard = ({ icon, title, description }: { icon: string; title: string; description: string }) => {
        return (
            <div style={{
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                padding: "24px",
                border: "1px solid #e2e8f0",
                textAlign: "center",
                flex: "1 1 150px",
                minWidth: "150px",
                maxWidth: "220px",
            }}>
                <div style={{
                    fontSize: "32px",
                    marginBottom: "12px",
                }}>
                    {icon}
                </div>
                <h3 style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#2d3748",
                    marginBottom: "8px",
                }}>
                    {title}
                </h3>
                <p style={{
                    fontSize: "14px",
                    color: "#718096",
                    lineHeight: "1.5",
                }}>
                    {description}
                </p>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                <div className="px-4 py-6 sm:px-0">
                    <div style={containerStyle}>
                        <div style={headerStyle}>
                            <h1 style={titleStyle}>
                                {activeTab === "generate" && !canAccessGenerate && "🌟 Meal Planning Pro"}
                                {activeTab === "generate" && canAccessGenerate &&
                                    "🚀 Generate Meal Plan"}
                                {activeTab === "editor" && "✏️ Edit Meal Plan"}
                                {activeTab === "history" &&
                                    "📋 Meal Plan History"}
                                {activeTab === "tools" && "🛠️ Other Tools"}
                            </h1>
                            <p style={descriptionStyle}>
                                {activeTab === "generate" && !canAccessGenerate && 
                                    "Upgrade to access AI-powered meal planning and nutrition tracking."}
                                {activeTab === "generate" && canAccessGenerate &&
                                    "Create a personalized meal plan based on your dietary preferences and goals."}
                                {activeTab === "editor" &&
                                    (selectedMealPlan
                                        ? "Customize your meal plan by adjusting ingredients and portions."
                                        : "Build a meal plan manually by adding ingredients, or select one from history to edit.")}
                                {activeTab === "history" &&
                                    "Browse and manage your saved meal plans."}
                                {activeTab === "tools" &&
                                    "Additional AI-powered tools to enhance your meal planning experience."}
                            </p>
                        </div>

                        <div style={tabsContainerStyle}>
                            {tabs.map((tab) => {
                                // Determine if tab should be locked based on type
                                // Only lock history/editor/tools for users without access
                                const isLocked = 
                                    ((tab.id === "history" || tab.id === "editor" || tab.id === "tools") && !canAccessHistoryEditor);
                                
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => handleTabClick(tab.id)}
                                        style={{
                                            ...(activeTab === tab.id
                                                ? activeTabStyle
                                                : tabButtonStyle),
                                            ...(isLocked && {
                                                cursor: "not-allowed",
                                                opacity: 0.6,
                                            }),
                                        }}
                                        onMouseEnter={(e) => {
                                            if (activeTab !== tab.id && !isLocked) {
                                                e.currentTarget.style.backgroundColor =
                                                    "#f3f4f6";
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (activeTab !== tab.id && !isLocked) {
                                                e.currentTarget.style.backgroundColor =
                                                    "transparent";
                                            }
                                        }}
                                        title={isLocked ? 
                                            (tab.id === "generate" 
                                                ? "Subscribe to generate AI meal plans" 
                                                : "Subscribe to access meal planning features") 
                                            : undefined}
                                        disabled={isLocked}
                                    >
                                        {tab.label}
                                        {isLocked && " 🔒"}
                                    </button>
                                );
                            })}
                        </div>

                        <div style={contentStyle}>
                            {loadingMealPlan || mealPlanError ? (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: "64px",
                                        color: mealPlanError
                                            ? "#dc2626"
                                            : "#6b7280",
                                    }}
                                >
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ marginBottom: "16px" }}>
                                            <span style={{ fontSize: "32px" }}>
                                                {mealPlanError ? "❌" : "📋"}
                                            </span>
                                        </div>
                                        <div>
                                            {mealPlanError
                                                ? mealPlanError +
                                                  " - Redirecting..."
                                                : "Loading meal plan..."}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                renderContent()
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
